/**
 * Cache RAM del snapshot de catálogo (ADR-017 — todas las plataformas).
 * Nombre histórico de APIs: patchWebCatalog* se mantiene por compat de call sites.
 */
import { publishCatalogSynced } from '../../sync/core/catalogSyncListeners';

type WebCatalogProductRow = {
  id: string;
  name: string;
  price: number;
  sku: string | null;
  barcode: string | null;
  category_id: string | null;
  is_active?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  item_type?: string | null;
  unit_of_measure?: string | null;
  sell_by_weight?: boolean | null;
  duration_minutes?: number | null;
  buffer_before_minutes?: number | null;
  buffer_after_minutes?: number | null;
  parent_product_id?: string | null;
  is_misc?: boolean | null;
  organization_id?: string | null;
  brand_id?: string | null;
  preferred_supplier_id?: string | null;
  supplier_item_code?: string | null;
  internal_notes?: string | null;
  tax_class?: string | null;
  image_url?: string | null;
  video_url?: string | null;
  public_description?: string | null;
  is_public?: boolean | null;
  active_ingredient?: string | null;
  usage_tags?: string[] | null;
  allergens?: string[] | null;
  booking_mode?: string | null;
  evaluation_product_id?: string | null;
  booking_payment_mode?: string | null;
  booking_deposit_amount?: number | null;
  booking_deposit_percent?: number | null;
  kitchen_destination?: string | null;
  prep_minutes?: number | null;
  menu_available?: boolean | null;
  visible_on_menu_carta?: boolean | null;
  min_order_qty?: number | null;
  max_order_qty?: number | null;
  combo_valid_from?: string | null;
  combo_valid_until?: string | null;
};

type WebCatalogStockRow = {
  id?: string;
  product_id: string;
  branch_id: string;
  quantity: number;
  min_quantity: number;
  avg_cost?: number | null;
  expires_at?: string | null;
  variant_id?: string | null;
};

export type WebCatalogStockTransferRow = {
  id: string;
  product_id: string;
  from_branch_id: string;
  to_branch_id: string;
  quantity: number;
  unit_cost: number | null;
  performed_at: string;
  status: string;
  variant_id?: string | null;
};

export type WebCatalogStockLotRow = {
  id: string;
  product_id: string;
  branch_id: string;
  variant_id?: string | null;
  remaining_quantity: number;
  expires_at: string | null;
  depleted_at: string | null;
  received_at: string;
  unit_cost: number;
  lot_ref?: string | null;
  origin?: string | null;
};

export type WebCatalogCategoryRow = {
  id: string;
  organization_id: string;
  slug?: string | null;
  name: string;
  is_active?: boolean;
  is_restricted?: boolean;
  sort_order?: number | null;
  color?: string | null;
  applies_to?: string | null;
  parent_id?: string | null;
  code?: string | null;
  default_margin_percent?: number | null;
  description?: string | null;
  image_url?: string | null;
  video_url?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  deleted_at?: string | null;
};

export type WebCatalogBrandRow = {
  id: string;
  organization_id?: string | null;
  name?: string | null;
  code?: string | null;
  website?: string | null;
  default_markup_percent?: number | null;
  is_active?: boolean;
  deleted_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type WebCatalogSupplierRow = {
  id: string;
  organization_id?: string | null;
  name?: string | null;
  code?: string | null;
  tax_id?: string | null;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  payment_terms?: string | null;
  lead_time_days?: number | null;
  minimum_order_amount?: number | null;
  website?: string | null;
  notes?: string | null;
  account_number?: string | null;
  is_active?: boolean;
  deleted_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

/** Cache en memoria del último snapshot de catálogo (web sin SQLite confiable). */
export type CatalogRamSnapshotCache = {
  products: WebCatalogProductRow[];
  stocks: WebCatalogStockRow[];
  stockTransfers: WebCatalogStockTransferRow[];
  stockLots: WebCatalogStockLotRow[];
  categories: WebCatalogCategoryRow[];
  brands: WebCatalogBrandRow[];
  suppliers: WebCatalogSupplierRow[];
  promotions: WebCatalogPromotionRow[];
  promotionTargets: WebCatalogPromotionTargetRow[];
  productVariants: WebCatalogProductVariantRow[];
  priceLists: WebCatalogPriceListRow[];
  priceListItems: WebCatalogPriceListItemRow[];
  categoriesCount: number;
  suppliersCount: number;
  brandsCount: number;
  fetchedAt: number;
};

export type WebCatalogPromotionRow = {
  id: string;
  organization_id?: string | null;
  name?: string | null;
  description?: string | null;
  kind?: string | null;
  scope?: string | null;
  value_percent?: number | null;
  value_fixed?: number | null;
  combo_buy_qty?: number | null;
  combo_pay_qty?: number | null;
  combo_bundle_price?: number | null;
  starts_at?: string | null;
  ends_at?: string | null;
  is_active?: boolean;
  branch_ids?: unknown;
  apply_mode?: string | null;
  promo_code?: string | null;
  priority?: number | null;
  stackable?: boolean;
  channels?: unknown;
  conditions_json?: unknown;
  created_at?: string | null;
  updated_at?: string | null;
  deleted_at?: string | null;
};

export type WebCatalogPromotionTargetRow = {
  id: string;
  promotion_id: string;
  target_type?: string | null;
  target_id?: string | null;
  created_at?: string | null;
};

export type WebCatalogProductVariantRow = {
  id: string;
  [key: string]: unknown;
};

export type WebCatalogPriceListRow = {
  id: string;
  [key: string]: unknown;
};

export type WebCatalogPriceListItemRow = {
  id: string;
  [key: string]: unknown;
};

let cached: CatalogRamSnapshotCache | null = null;

/** Un pull completo en vuelo puede nacer antes de un alta/edición y pisar la fila local. */
const LOCAL_PATCH_PRESERVE_MS = 60_000;
let lastLocalStockPatchAt = 0;
const patchedStockKeys = new Set<string>();
let lastLocalProductPatchAt = 0;
const patchedProductIds = new Set<string>();
let lastLocalTransferPatchAt = 0;
const patchedTransferIds = new Set<string>();
let lastLocalPromotionPatchAt = 0;
const patchedPromotionIds = new Set<string>();

const stockRowVariantKey = (row: WebCatalogStockRow): string => {
  const variantId = (row as { variant_id?: string | null }).variant_id;
  return variantId && variantId.trim() ? variantId.trim() : '';
};

export function stockRowCacheKey(
  productId: string,
  branchId: string,
  variantId?: string | null,
): string {
  const variantKey = variantId && variantId.trim() ? variantId.trim() : '';
  return `${productId}::${branchId}::${variantKey}`;
}

export function overlayPatchedStockRows(
  incoming: WebCatalogStockRow[],
  previous: WebCatalogStockRow[],
  keys: ReadonlySet<string>,
): WebCatalogStockRow[] {
  if (keys.size === 0 || previous.length === 0) return incoming;
  const next = [...incoming];
  for (const prev of previous) {
    const key = stockRowCacheKey(
      prev.product_id,
      prev.branch_id,
      (prev as { variant_id?: string | null }).variant_id,
    );
    if (!keys.has(key)) continue;
    const prevVariantKey = stockRowVariantKey(prev);
    const index = next.findIndex(
      (item) =>
        item.product_id === prev.product_id &&
        item.branch_id === prev.branch_id &&
        stockRowVariantKey(item) === prevVariantKey
    );
    if (index >= 0) {
      next[index] = prev;
    } else {
      next.push(prev);
    }
  }
  return next;
}

export function overlayPatchedRowsById<T extends { id: string; updated_at?: string | null }>(
  incoming: T[],
  previous: T[],
  ids: ReadonlySet<string>,
): T[] {
  if (ids.size === 0 || previous.length === 0) return incoming;
  const next = [...incoming];
  for (const prev of previous) {
    if (!ids.has(prev.id)) continue;
    const index = next.findIndex((item) => item.id === prev.id);
    if (index >= 0) {
      const incomingAt = next[index]?.updated_at;
      const prevAt = prev.updated_at;
      // Write remoto más nuevo (otro empleado / API): no reponer el parche local.
      if (incomingAt && prevAt && incomingAt > prevAt) {
        continue;
      }
      next[index] = prev;
    } else {
      next.push(prev);
    }
  }
  return next;
}

function isLocalPatchFresh(at: number, keysSize: number): boolean {
  return keysSize > 0 && at > 0 && Date.now() - at < LOCAL_PATCH_PRESERVE_MS;
}

function shouldPreserveLocalStockPatches(): boolean {
  return isLocalPatchFresh(lastLocalStockPatchAt, patchedStockKeys.size);
}

function shouldPreserveLocalProductPatches(): boolean {
  return isLocalPatchFresh(lastLocalProductPatchAt, patchedProductIds.size);
}

function shouldPreserveLocalTransferPatches(): boolean {
  return isLocalPatchFresh(lastLocalTransferPatchAt, patchedTransferIds.size);
}

function shouldPreserveLocalPromotionPatches(): boolean {
  return isLocalPatchFresh(lastLocalPromotionPatchAt, patchedPromotionIds.size);
}

function rememberLocalPromotionPatch(promotionId: string): void {
  patchedPromotionIds.add(promotionId);
  lastLocalPromotionPatchAt = Date.now();
}

function rememberLocalProductPatch(productId: string): void {
  patchedProductIds.add(productId);
  lastLocalProductPatchAt = Date.now();
}

function rememberLocalTransferPatch(transferId: string): void {
  patchedTransferIds.add(transferId);
  lastLocalTransferPatchAt = Date.now();
}

function applyLocalPatchOverlays(previous: {
  stocks: WebCatalogStockRow[];
  products: WebCatalogProductRow[];
  stockTransfers: WebCatalogStockTransferRow[];
  promotions: WebCatalogPromotionRow[];
}): void {
  if (!cached) return;
  if (shouldPreserveLocalStockPatches()) {
    cached.stocks = overlayPatchedStockRows(cached.stocks, previous.stocks, patchedStockKeys);
  }
  if (shouldPreserveLocalProductPatches()) {
    cached.products = overlayPatchedRowsById(cached.products, previous.products, patchedProductIds);
  } else {
    // Safety net until prod includes inactive products in the snapshot:
    // a paused product patched locally must not vanish after LOCAL_PATCH_PRESERVE_MS.
    cached.products = overlayMissingInactivePatchedProducts(
      cached.products,
      previous.products,
      patchedProductIds,
    );
  }
  if (shouldPreserveLocalTransferPatches()) {
    cached.stockTransfers = overlayPatchedRowsById(
      cached.stockTransfers,
      previous.stockTransfers,
      patchedTransferIds,
    );
  }
  if (shouldPreserveLocalPromotionPatches()) {
    cached.promotions = overlayPatchedRowsById(
      cached.promotions,
      previous.promotions,
      patchedPromotionIds,
    );
  }
}

/** Keep locally paused products that the server snapshot still omits (active-only RPC). */
export function overlayMissingInactivePatchedProducts(
  incoming: WebCatalogProductRow[],
  previous: WebCatalogProductRow[],
  ids: ReadonlySet<string>,
): WebCatalogProductRow[] {
  if (ids.size === 0 || previous.length === 0) return incoming;
  const next = [...incoming];
  for (const prev of previous) {
    if (!ids.has(prev.id)) continue;
    if (prev.is_active !== false) continue;
    if (next.some((item) => item.id === prev.id)) continue;
    next.push(prev);
  }
  return next;
}

function publishCatalogCacheMutation(): void {
  publishCatalogSynced({
    productsCount: cached?.products.length ?? 0,
    categoriesCount: cached?.categories.length ?? 0,
    suppliersCount: cached?.suppliers.length ?? 0,
    brandsCount: cached?.brands.length ?? 0,
  });
}

export function setCatalogRamSnapshotCache(payload: {
  products?: WebCatalogProductRow[];
  stocks?: WebCatalogStockRow[];
  stock_transfers?: WebCatalogStockTransferRow[];
  stock_lots?: WebCatalogStockLotRow[];
  categories?: unknown[];
  suppliers?: unknown[];
  brands?: unknown[];
  promotions?: unknown[];
  promotion_targets?: unknown[];
  product_variants?: unknown[];
  price_lists?: unknown[];
  price_list_items?: unknown[];
}): void {
  const previous = {
    stocks: cached?.stocks ?? [],
    products: cached?.products ?? [],
    stockTransfers: cached?.stockTransfers ?? [],
    promotions: cached?.promotions ?? [],
  };
  cached = {
    products: Array.isArray(payload.products) ? payload.products : [],
    stocks: Array.isArray(payload.stocks) ? payload.stocks : [],
    stockTransfers: Array.isArray(payload.stock_transfers) ? payload.stock_transfers : [],
    stockLots: Array.isArray(payload.stock_lots) ? payload.stock_lots : [],
    categories: Array.isArray(payload.categories) ? (payload.categories as WebCatalogCategoryRow[]) : [],
    brands: Array.isArray(payload.brands) ? (payload.brands as WebCatalogBrandRow[]) : [],
    suppliers: Array.isArray(payload.suppliers) ? (payload.suppliers as WebCatalogSupplierRow[]) : [],
    promotions: Array.isArray(payload.promotions)
      ? (payload.promotions as WebCatalogPromotionRow[])
      : [],
    promotionTargets: Array.isArray(payload.promotion_targets)
      ? (payload.promotion_targets as WebCatalogPromotionTargetRow[])
      : [],
    productVariants: Array.isArray(payload.product_variants)
      ? (payload.product_variants as WebCatalogProductVariantRow[])
      : [],
    priceLists: Array.isArray(payload.price_lists)
      ? (payload.price_lists as WebCatalogPriceListRow[])
      : [],
    priceListItems: Array.isArray(payload.price_list_items)
      ? (payload.price_list_items as WebCatalogPriceListItemRow[])
      : [],
    categoriesCount: Array.isArray(payload.categories) ? payload.categories.length : 0,
    suppliersCount: Array.isArray(payload.suppliers) ? payload.suppliers.length : 0,
    brandsCount: Array.isArray(payload.brands) ? payload.brands.length : 0,
    fetchedAt: Date.now(),
  };
  applyLocalPatchOverlays(previous);
  publishCatalogCacheMutation();
}

function mergeRowsById<T extends { id: string }>(current: T[], incoming: T[]): T[] {
  if (incoming.length === 0) return current;
  const next = [...current];
  for (const row of incoming) {
    if (!row?.id) continue;
    const index = next.findIndex((item) => item.id === row.id);
    if (index >= 0) {
      next[index] = { ...next[index], ...row };
    } else {
      next.push(row);
    }
  }
  return next;
}

function mergeStockRows(current: WebCatalogStockRow[], incoming: WebCatalogStockRow[]): WebCatalogStockRow[] {
  if (incoming.length === 0) return current;
  const next = [...current];
  for (const row of incoming) {
    const rowVariantKey = stockRowVariantKey(row);
    const index = next.findIndex(
      (item) =>
        item.product_id === row.product_id &&
        item.branch_id === row.branch_id &&
        stockRowVariantKey(item) === rowVariantKey
    );
    if (index >= 0) {
      next[index] = row;
    } else {
      next.push(row);
    }
  }
  return next;
}

/** Fusiona un pull incremental sin pisar entidades que no vinieron en el delta. */
export function mergeCatalogRamSnapshotCache(payload: {
  products?: WebCatalogProductRow[];
  stocks?: WebCatalogStockRow[];
  stock_transfers?: WebCatalogStockTransferRow[];
  stock_lots?: WebCatalogStockLotRow[];
  categories?: unknown[];
  suppliers?: unknown[];
  brands?: unknown[];
  promotions?: unknown[];
  promotion_targets?: unknown[];
  product_variants?: unknown[];
  price_lists?: unknown[];
  price_list_items?: unknown[];
}): void {
  if (!cached) {
    setCatalogRamSnapshotCache(payload);
    publishCatalogCacheMutation();
    return;
  }

  const categories = Array.isArray(payload.categories)
    ? (payload.categories as WebCatalogCategoryRow[])
    : [];
  const brands = Array.isArray(payload.brands) ? (payload.brands as WebCatalogBrandRow[]) : [];
  const suppliers = Array.isArray(payload.suppliers)
    ? (payload.suppliers as WebCatalogSupplierRow[])
    : [];
  const promotions = Array.isArray(payload.promotions)
    ? (payload.promotions as WebCatalogPromotionRow[])
    : [];
  const promotionTargets = Array.isArray(payload.promotion_targets)
    ? (payload.promotion_targets as WebCatalogPromotionTargetRow[])
    : [];
  const productVariants = Array.isArray(payload.product_variants)
    ? (payload.product_variants as WebCatalogProductVariantRow[])
    : [];
  const priceLists = Array.isArray(payload.price_lists)
    ? (payload.price_lists as WebCatalogPriceListRow[])
    : [];
  const priceListItems = Array.isArray(payload.price_list_items)
    ? (payload.price_list_items as WebCatalogPriceListItemRow[])
    : [];

  const previous = {
    stocks: cached.stocks,
    products: cached.products,
    stockTransfers: cached.stockTransfers,
    stockLots: cached.stockLots,
    promotions: cached.promotions,
  };
  cached = {
    ...cached,
    products: Array.isArray(payload.products)
      ? mergeRowsById(cached.products, payload.products)
      : cached.products,
    stocks: Array.isArray(payload.stocks) ? mergeStockRows(cached.stocks, payload.stocks) : cached.stocks,
    stockTransfers: Array.isArray(payload.stock_transfers)
      ? mergeRowsById(cached.stockTransfers, payload.stock_transfers)
      : cached.stockTransfers,
    stockLots: Array.isArray(payload.stock_lots)
      ? mergeRowsById(cached.stockLots, payload.stock_lots)
      : cached.stockLots,
    categories: categories.length > 0 ? mergeRowsById(cached.categories, categories) : cached.categories,
    brands: brands.length > 0 ? mergeRowsById(cached.brands, brands) : cached.brands,
    suppliers: suppliers.length > 0 ? mergeRowsById(cached.suppliers, suppliers) : cached.suppliers,
    promotions: promotions.length > 0 ? mergeRowsById(cached.promotions, promotions) : cached.promotions,
    promotionTargets:
      promotionTargets.length > 0
        ? mergeRowsById(cached.promotionTargets, promotionTargets)
        : cached.promotionTargets,
    productVariants:
      productVariants.length > 0
        ? mergeRowsById(cached.productVariants, productVariants)
        : cached.productVariants,
    priceLists: priceLists.length > 0 ? mergeRowsById(cached.priceLists, priceLists) : cached.priceLists,
    priceListItems:
      priceListItems.length > 0
        ? mergeRowsById(cached.priceListItems, priceListItems)
        : cached.priceListItems,
    categoriesCount: cached.categories.length,
    suppliersCount: cached.suppliers.length,
    brandsCount: cached.brands.length,
    fetchedAt: Date.now(),
  };
  applyLocalPatchOverlays(previous);
  publishCatalogCacheMutation();
}

export function getCatalogRamSnapshotCache(): CatalogRamSnapshotCache | null {
  return cached;
}

export function clearCatalogRamSnapshotCache(): void {
  cached = null;
  lastLocalStockPatchAt = 0;
  patchedStockKeys.clear();
  lastLocalProductPatchAt = 0;
  patchedProductIds.clear();
  lastLocalTransferPatchAt = 0;
  patchedTransferIds.clear();
  lastLocalPromotionPatchAt = 0;
  patchedPromotionIds.clear();
}

/** Mantiene la UI web alineada con SQLite tras crear/editar productos localmente. */
export function patchWebCatalogProductInCache(
  productId: string,
  patch: Partial<WebCatalogProductRow>
): void {
  if (!cached?.products.length) return;
  const index = cached.products.findIndex((p) => p.id === productId);
  if (index < 0) return;
  const current = cached.products[index];
  cached.products[index] = {
    ...current,
    ...patch,
    id: productId,
    updated_at: patch.updated_at ?? new Date().toISOString(),
  };
  rememberLocalProductPatch(productId);
  cached.fetchedAt = Date.now();
  publishCatalogCacheMutation();
}

/** Mantiene la UI web alineada tras activar/desactivar promociones localmente. */
export function patchWebCatalogPromotionInCache(
  promotionId: string,
  patch: Partial<WebCatalogPromotionRow>
): void {
  if (!cached?.promotions.length) return;
  const index = cached.promotions.findIndex((p) => p.id === promotionId);
  if (index < 0) return;
  const current = cached.promotions[index];
  cached.promotions[index] = {
    ...current,
    ...patch,
    id: promotionId,
    updated_at: patch.updated_at ?? new Date().toISOString(),
  };
  rememberLocalPromotionPatch(promotionId);
  cached.fetchedAt = Date.now();
  publishCatalogCacheMutation();
}

/**
 * Alta/edición local: upsert completo en RAM para que el listado no dependa
 * de un pull remoto que todavía no ve el write (replica lag / snapshot stale).
 */
export function upsertWebCatalogPromotionInCache(
  promotion: WebCatalogPromotionRow,
  targets?: WebCatalogPromotionTargetRow[],
): void {
  if (!cached || !promotion?.id) return;
  const index = cached.promotions.findIndex((p) => p.id === promotion.id);
  const nextRow: WebCatalogPromotionRow = {
    ...(index >= 0 ? cached.promotions[index] : {}),
    ...promotion,
    id: promotion.id,
    updated_at: promotion.updated_at ?? new Date().toISOString(),
  };
  if (index >= 0) {
    cached.promotions[index] = nextRow;
  } else {
    cached.promotions.push(nextRow);
  }
  if (targets) {
    const rest = (cached.promotionTargets ?? []).filter((t) => t.promotion_id !== promotion.id);
    cached.promotionTargets = [...rest, ...targets.filter((t) => t?.id && t.promotion_id)];
  }
  rememberLocalPromotionPatch(promotion.id);
  cached.fetchedAt = Date.now();
  publishCatalogCacheMutation();
}

/** Tras alta/edición local de lista de precio — evita UI stale si el sync se coalescea con un pull pre-write. */
export function upsertWebCatalogPriceListInCache(
  list: WebCatalogPriceListRow,
  items?: WebCatalogPriceListItemRow[],
): void {
  if (!cached || !list?.id) return;
  const index = cached.priceLists.findIndex((row) => row.id === list.id);
  const nextRow: WebCatalogPriceListRow = {
    ...(index >= 0 ? cached.priceLists[index] : {}),
    ...list,
    id: list.id,
    updated_at: list.updated_at ?? new Date().toISOString(),
  };
  if (index >= 0) {
    cached.priceLists[index] = nextRow;
  } else {
    cached.priceLists.push(nextRow);
  }
  if (items) {
    const rest = (cached.priceListItems ?? []).filter(
      (item) => String(item.price_list_id ?? '') !== list.id,
    );
    cached.priceListItems = [...rest, ...items.filter((item) => item?.id)];
  }
  cached.fetchedAt = Date.now();
  publishCatalogCacheMutation();
}

/** Tras alta local en web, el snapshot en memoria debe incluir el producto para POS/barcode. */
export function appendWebCatalogProductToCache(
  product: WebCatalogProductRow,
  stock?: WebCatalogStockRow
): void {
  if (!cached) return;
  const existingIndex = cached.products.findIndex((p) => p.id === product.id);
  if (existingIndex >= 0) {
    patchWebCatalogProductInCache(product.id, product);
  } else {
    cached.products.push(product);
    rememberLocalProductPatch(product.id);
  }
  if (stock) {
    patchWebCatalogStockInCache(
      stock.product_id,
      stock.branch_id,
      stock.quantity,
      stock.id,
      stock.avg_cost ?? undefined,
    );
  } else {
    cached.fetchedAt = Date.now();
    publishCatalogCacheMutation();
  }
}

export function appendWebCatalogBrandToCache(brand: WebCatalogBrandRow): void {
  if (!cached) return;
  const index = cached.brands.findIndex((b) => b.id === brand.id);
  if (index >= 0) {
    cached.brands[index] = { ...cached.brands[index], ...brand };
  } else {
    cached.brands.push(brand);
    cached.brandsCount = cached.brands.length;
  }
  cached.fetchedAt = Date.now();
  publishCatalogCacheMutation();
}

export function appendWebCatalogCategoryToCache(category: WebCatalogCategoryRow): void {
  if (!cached) return;
  const index = cached.categories.findIndex((c) => c.id === category.id);
  if (index >= 0) {
    cached.categories[index] = { ...cached.categories[index], ...category };
  } else {
    cached.categories.push(category);
    cached.categoriesCount = cached.categories.length;
  }
  cached.fetchedAt = Date.now();
  publishCatalogCacheMutation();
}

/**
 * Refleja un reorder local (org_category_settings.sort_order) en el snapshot RAM.
 * Sin esto los readers siguen viendo el orden viejo hasta el próximo pull.
 */
export function patchWebCatalogCategorySortOrders(
  items: { id: string; sortOrder: number }[],
): void {
  if (!cached || items.length === 0) return;
  const sortById = new Map(items.map((item) => [item.id, item.sortOrder]));
  let changed = false;
  cached.categories = cached.categories.map((row) => {
    const sortOrder = sortById.get(row.id);
    if (sortOrder === undefined) return row;
    changed = true;
    return { ...row, sort_order: sortOrder };
  });
  if (!changed) return;
  cached.fetchedAt = Date.now();
  publishCatalogCacheMutation();
}

export function appendWebCatalogSupplierToCache(supplier: WebCatalogSupplierRow): void {
  if (!cached) return;
  const index = cached.suppliers.findIndex((s) => s.id === supplier.id);
  if (index >= 0) {
    cached.suppliers[index] = { ...cached.suppliers[index], ...supplier };
  } else {
    cached.suppliers.push(supplier);
    cached.suppliersCount = cached.suppliers.length;
  }
  cached.fetchedAt = Date.now();
  publishCatalogCacheMutation();
}

/** Tras venta/ajuste local en web, el snapshot en memoria debe reflejar el stock SQLite. */
function mutateCachedStockQuantity(
  productId: string,
  branchId: string,
  quantity: number,
  stockId?: string,
  avgCost?: number,
  variantId?: string | null,
): void {
  if (!cached) return;
  const stockIndex = cached.stocks.findIndex((s) => {
    if (s.product_id !== productId || s.branch_id !== branchId) return false;
    const rowVariantId = (s as { variant_id?: string | null }).variant_id ?? null;
    if (variantId) return rowVariantId === variantId;
    return rowVariantId == null || rowVariantId === '';
  });
  if (stockIndex >= 0) {
    cached.stocks[stockIndex] = {
      ...cached.stocks[stockIndex],
      quantity,
      ...(stockId ? { id: stockId } : {}),
      ...(avgCost != null ? { avg_cost: avgCost } : {}),
    };
  } else {
    cached.stocks.push({
      id: stockId ?? crypto.randomUUID(),
      product_id: productId,
      branch_id: branchId,
      quantity,
      min_quantity: 0,
      ...(avgCost != null ? { avg_cost: avgCost } : {}),
      ...(variantId ? { variant_id: variantId } : {}),
    } as typeof cached.stocks[number]);
  }
  patchedStockKeys.add(stockRowCacheKey(productId, branchId, variantId));
}

export function patchWebCatalogStockInCache(
  productId: string,
  branchId: string,
  quantity: number,
  stockId?: string,
  avgCost?: number,
  variantId?: string | null,
): void {
  if (!cached) return;
  mutateCachedStockQuantity(productId, branchId, quantity, stockId, avgCost, variantId);
  lastLocalStockPatchAt = Date.now();
  cached.fetchedAt = Date.now();
  publishCatalogCacheMutation();
}

/** Varias celdas de stock en un solo publish (p. ej. produce/disassemble BOM). */
export function patchWebCatalogStocksBatchInCache(
  patches: Array<{
    productId: string;
    branchId: string;
    quantity: number;
    stockId?: string;
    avgCost?: number;
    variantId?: string | null;
  }>,
): void {
  if (!cached || patches.length === 0) return;
  for (const patch of patches) {
    mutateCachedStockQuantity(
      patch.productId,
      patch.branchId,
      patch.quantity,
      patch.stockId,
      patch.avgCost,
      patch.variantId,
    );
  }
  lastLocalStockPatchAt = Date.now();
  cached.fetchedAt = Date.now();
  publishCatalogCacheMutation();
}

export function appendWebCatalogProductVariantToCache(variant: WebCatalogProductVariantRow): void {
  if (!cached) return;
  const index = cached.productVariants.findIndex((row) => String(row.id) === String(variant.id));
  if (index >= 0) {
    cached.productVariants[index] = { ...cached.productVariants[index], ...variant };
  } else {
    cached.productVariants.push(variant);
  }
  cached.fetchedAt = Date.now();
  publishCatalogCacheMutation();
}

export function patchWebCatalogStockTransferInCache(
  transferId: string,
  patch: Partial<WebCatalogStockTransferRow>,
): void {
  if (!cached) return;
  const index = cached.stockTransfers.findIndex((row) => row.id === transferId);
  if (index >= 0) {
    cached.stockTransfers[index] = { ...cached.stockTransfers[index], ...patch };
  } else if (patch.product_id && patch.from_branch_id && patch.to_branch_id) {
    cached.stockTransfers.push({
      id: transferId,
      product_id: String(patch.product_id),
      from_branch_id: String(patch.from_branch_id),
      to_branch_id: String(patch.to_branch_id),
      quantity: Number(patch.quantity ?? 0),
      unit_cost: patch.unit_cost ?? null,
      performed_at: String(patch.performed_at ?? new Date().toISOString()),
      status: String(patch.status ?? 'sent'),
      variant_id: patch.variant_id ?? null,
    });
  } else {
    return;
  }
  rememberLocalTransferPatch(transferId);
  cached.fetchedAt = Date.now();
  publishCatalogCacheMutation();
}
